import React, { useEffect, useMemo, useState } from 'react';
import { getOptimizedPublicImageSrc, isOptimizablePublicImage } from '../utils/imageOptimization';

type OptimizedImageProps = React.ImgHTMLAttributes<HTMLImageElement>;

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt = '',
  loading,
  decoding,
  onError,
  ...rest
}) => {
  const finalLoading = loading ?? 'lazy';
  const finalDecoding = decoding ?? 'async';
  const originalSrc = typeof src === 'string' ? src : '';
  const optimizedSrc = useMemo(
    () => getOptimizedPublicImageSrc(originalSrc),
    [originalSrc],
  );
  const canUseOptimized = useMemo(
    () => isOptimizablePublicImage(originalSrc) && optimizedSrc && optimizedSrc !== originalSrc,
    [originalSrc, optimizedSrc],
  );
  const [resolvedSrc, setResolvedSrc] = useState(
    canUseOptimized ? optimizedSrc : originalSrc,
  );

  useEffect(() => {
    setResolvedSrc(canUseOptimized ? optimizedSrc : originalSrc);
  }, [canUseOptimized, optimizedSrc, originalSrc]);

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      loading={finalLoading}
      decoding={finalDecoding}
      onError={(event) => {
        if (canUseOptimized && resolvedSrc !== originalSrc) {
          setResolvedSrc(originalSrc);
          return;
        }
        onError?.(event);
      }}
      {...rest}
    />
  );
};

export default OptimizedImage;
